import * as React from 'react';
import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IEventDetailsDTO } from '../../../../../../../api/events.dao';
import { IGlobalStorage } from '../../../../../../../infrastructure/global-storage';
import { FormattedMessage } from 'react-intl';
import { SHORT_PLAIN_TEXT_200_LENGTH, SHORT_PLAIN_TEXT_64_LENGTH, ZOOM_EMAIL_MAX_LENGTH } from '../../../../../../../constants/common.constants';
import { BASE_EMAIL_VALIDATION_REG_EXP } from '../../../../../../../constants/regex.constants';
import { IRecurringZoomMeetingDTO, IZoomGroupDTO, ZoomMeetingType } from '../../../../../../../api/zoom.dao';
import { getEnvPostfix } from '../../../../../../../infrastructure/utils/common.utils';

export const emailPostfix = `@isv${getEnvPostfix()}openexc.com`;

class RecurringZoomMeetingFormDataVM {
    @mobx.observable
    public event: IEventDetailsDTO;
    @mobx.observable
    private topic: string = '';
    @mobx.observable
    private email: string = '';
    @mobx.observable
    private username: string = '';
    @mobx.observable
    private selectedGroupId: number;
    @mobx.observable
    public validationErrors: Record<string, JSX.Element>;
    @mobx.observable
    public allowedZoomGroups: IZoomGroupDTO[];
    constructor(
        private readonly accountId: number,
        @IoC.Inject private readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage)) {
        this.allowedZoomGroups = [];
        this.cleanup();
    }

    public getMeetingTopic = (): string => {
        return this.topic;
    };

    @mobx.action
    public setMeetingTopic = (topic: string): void => {
        this.topic = topic;
    };

    @mobx.action
    public setAllowedZoomGroups = (allowedZoomGroupsProps: IZoomGroupDTO[]): void => {
        this.allowedZoomGroups = allowedZoomGroupsProps || [];
    };

    @mobx.action
    public setZoomGroupData = (allowedZoomGroupsProps: IZoomGroupDTO[]): void => {
        const sortedAllowedZoomGroups = allowedZoomGroupsProps.slice().sort((a, b) => a.name.localeCompare(b.name));
        this.setAllowedZoomGroups(sortedAllowedZoomGroups);
        this.setGroup(sortedAllowedZoomGroups?.[0]?.zoomGroupId || null);
    };

    public getEmail = (): string => {
        return this.email;
    };

    @mobx.action
    public setEmail = (email: string): void => {
        this.email = email;
    };

    public getUserName = (): string => {
        return this.username;
    };

    @mobx.action
    public setUserName = (username: string): void => {
        this.username = username;
    };

    public getGroup = (): number => {
        return this.selectedGroupId;
    };

    @mobx.action
    public setGroup = (groupId: number): void => {
        this.selectedGroupId = groupId;
    };

    @mobx.action
    public checkMeetingTopic = (): void => {
        if (this.validationErrors.topic) {
            delete this.validationErrors.topic;
        }
        if (this.topic) {
            this.topic = this.topic.trim();
        }
        if (!this.topic) {
            this.validationErrors.topic = <FormattedMessage
                defaultMessage='This field is required'
                id='oe.error.required'
                description='Error message text'
            />;
            return;
        }
        if (this.topic.length > SHORT_PLAIN_TEXT_200_LENGTH) {
            this.validationErrors.topic = <FormattedMessage
                id='oe.attendee.MaxLengthIs'
                defaultMessage='Max length is {length} characters'
                description='Error label text'
                values={{
                    length: SHORT_PLAIN_TEXT_200_LENGTH
                }}
            />;
        }
    };

    @mobx.action
    public checkEmail = (): void => {
        if (this.validationErrors.email) {
            delete this.validationErrors.email;
        }
        if (this.email) {
            this.email = this.email.trim();
        }
        if (!this.email) {
            this.validationErrors.email = <FormattedMessage
                defaultMessage='This field is required'
                id='oe.error.required'
                description='Error message text'
            />;
            return;
        }
        const zoomEmailMaxLength = ZOOM_EMAIL_MAX_LENGTH - emailPostfix.length;
        if (this.email.length > zoomEmailMaxLength) {
            this.validationErrors.email = <FormattedMessage
                id='oe.attendee.MaxLengthIs'
                defaultMessage='Max length is {length} characters'
                description='Error label text'
                values={{
                    length: zoomEmailMaxLength
                }}
            />;
        }
        if (!BASE_EMAIL_VALIDATION_REG_EXP.test(this.email)) {
            this.validationErrors.email = <FormattedMessage
                id='oe.email.emailIsNotValid'
                defaultMessage='Must be a valid email address'
                description='Error message in user popover'
            />;
        }
    };


    @mobx.action
    public checkUserName = (): void => {
        if (this.validationErrors.username) {
            delete this.validationErrors.username;
        }
        if (this.username) {
            this.username = this.username.trim();
        }
        if (!this.username) {
            this.validationErrors.username = <FormattedMessage
                defaultMessage='This field is required'
                id='oe.error.required'
                description='Error message text'
            />;
            return;
        }
        if (this.username.length > SHORT_PLAIN_TEXT_64_LENGTH) {
            this.validationErrors.username = <FormattedMessage
                id='oe.attendee.MaxLengthIs'
                defaultMessage='Max length is {length} characters'
                description='Error label text'
                values={{
                    length: SHORT_PLAIN_TEXT_64_LENGTH
                }}
            />;
        }
    };


    public getFieldError = (fieldName: string): JSX.Element | undefined => {
        return this.validationErrors?.[fieldName];
    };

    @mobx.action
    public checkForm = (): boolean => {
        this.checkMeetingTopic();
        this.checkEmail();
        this.checkUserName();
        return !this.validationErrors.topic && !this.validationErrors.email && !this.validationErrors.username;
    };

    @mobx.action
    public cleanup = (): void => {
        this.event = this.globalStorage.getEvent();
        const resolvedTopic = this.event.eventTitleForPassport || this.event.name;
        this.topic = resolvedTopic;
        this.email = `OE_OnDemand_${this.event.eventId}`;
        this.username = resolvedTopic;
        this.selectedGroupId = null;
        this.validationErrors = {};
    };

  @mobx.action
  public getAPIDTO = (): IRecurringZoomMeetingDTO => {
    return {
        topic: this.topic,
        email: this.email + emailPostfix,
        username: this.username,
        zoomGroupId: this.selectedGroupId,
        type: ZoomMeetingType.RECURRING_NO_FIXED_MEETING,
        accountId: this.accountId
    };
  };
}

export { RecurringZoomMeetingFormDataVM };